import React, { useCallback, useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import {
  getPublicCreatorChallenge,
  DonateChallenge,
} from '../../../api/payment/donate-challenge.api';
import {
  clockSkewMs,
  countdownLabel,
  formatCa,
  formatDayMonth,
  challengeRuleSentence,
} from '../../../utils/donate-challenge';
import usePollWhile from '../../../hooks/use-poll-while/use-poll-while';

/**
 * Thử thách donate hiển thị cho NGƯỜI XEM trên trang creator.
 * Trước đây thử thách chỉ hiện trên overlay livestream — ai vào trang cá nhân mà không
 * xem stream thì không biết creator đang có thử thách gì, mất một điểm đẩy donate.
 * Không có nút Donate riêng ở đây — trang đã có đường donate sẵn.
 */
const CreatorDonateChallenge: React.FC<{ userId?: string }> = ({ userId }) => {
  const [challenge, setChallenge] = useState<DonateChallenge | null>(null);
  const [skewMs, setSkewMs] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [visible, setVisible] = useState(document.visibilityState === 'visible');

  const load = useCallback(() => {
    if (!userId) return;
    getPublicCreatorChallenge(userId)
      .then((c) => {
        setChallenge(c);
        setSkewMs(clockSkewMs(c.serverNow));
      })
      .catch(() => setChallenge(null));
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  usePollWhile(!!userId && visible, load, 30000);

  // Đếm ngược tại chỗ mỗi giây, không hỏi lại máy chủ liên tục.
  useEffect(() => {
    if (challenge?.status !== 'Scheduled' && challenge?.status !== 'Running') return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [challenge?.status]);

  // Chưa từng tạo / lỗi mạng -> BE trả exists=false, không hiện gì.
  if (!challenge?.exists) return null;

  if (challenge.status === 'Cancelled') {
    return (
      <div className="mt-8 box rounded-2xl p-4 md:p-6">
        <div className="flex items-center gap-2">
          <Trophy className="text-blue-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            Thử thách donate
          </h3>
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Thử thách {challenge.title} đã bị huỷ.
        </p>
      </div>
    );
  }

  if (challenge.status === 'Finalized') {
    return (
      <div className="mt-8 box rounded-2xl p-4 md:p-6">
        <div className="flex items-center gap-2">
          <Trophy className="text-blue-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            {challenge.finalizedAt
              ? `Quán quân thử thách ngày ${formatDayMonth(challenge.finalizedAt)}`
              : 'Kết quả thử thách donate'}
          </h3>
        </div>
        <p className="mt-1 break-words text-base font-semibold text-gray-800 dark:text-white">
          {challenge.title}
        </p>
        {challenge.winners?.length > 0 ? (
          <ul className="mt-3 flex flex-col gap-2">
            {challenge.winners.map((w) => (
              <li
                key={w.rank}
                className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300"
              >
                <span className="truncate">
                  #{w.rank} {w.displayName}
                </span>
                <span className="shrink-0">{formatCa(w.amount)} Ca</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Thử thách đã kết thúc, chưa có ai đạt.
          </p>
        )}
      </div>
    );
  }

  // Scheduled: đếm ngược tới lúc bắt đầu. Running: đếm ngược tới lúc kết thúc.
  const countdownTarget = challenge.status === 'Scheduled' ? challenge.startAt : challenge.endAt;
  const countdownMs = new Date(countdownTarget).getTime() - (now + skewMs);

  return (
    <div className="mt-8 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="text-blue-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            {challenge.status === 'Scheduled'
              ? 'Thử thách sắp diễn ra'
              : 'Thử thách đang diễn ra'}
          </h3>
        </div>
        <span className="shrink-0 text-sm font-bold text-blue-600 dark:text-blue-400">
          {countdownLabel(challenge.status === 'Scheduled' ? 'Scheduled' : 'Running', countdownMs, false)}
        </span>
      </div>

      <p className="mt-2 break-words text-base font-semibold text-gray-800 dark:text-white">
        {challenge.title}
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {challengeRuleSentence(challenge.mode, challenge.targetAmount, challenge.winnerCount)}
      </p>
      {challenge.rewardText && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Phần thưởng: {challenge.rewardText}
        </p>
      )}

      {challenge.status === 'Running' && challenge.leaderboard?.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {challenge.leaderboard.slice(0, 3).map((e) => (
            <li
              key={e.rank}
              className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300"
            >
              <span className="truncate">
                #{e.rank} {e.displayName}
              </span>
              <span className="shrink-0">{formatCa(e.amount)} Ca</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CreatorDonateChallenge;
